import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Camera, Play, Pause } from 'lucide-react';
import { gallery } from '../data/mock';

const SLIDE_MS = 3500;

export default function Gallery() {
  const [openIdx, setOpenIdx] = useState(null);
  const [playing, setPlaying] = useState(true);
  const barRef = useRef(null);

  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(
    () => setOpenIdx((i) => (i === null ? i : (i - 1 + gallery.length) % gallery.length)),
    []
  );
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? i : (i + 1) % gallery.length)),
    []
  );

  // Keyboard + body scroll lock while open
  useEffect(() => {
    if (openIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openIdx, close, prev, next]);

  // Auto-advance slideshow
  useEffect(() => {
    if (openIdx === null || !playing) return undefined;
    // restart progress bar animation
    if (barRef.current) {
      barRef.current.style.animation = 'none';
      // trigger reflow so animation restarts
      // eslint-disable-next-line no-unused-expressions
      barRef.current.offsetWidth;
      barRef.current.style.animation = `splash-progress ${SLIDE_MS}ms linear forwards`;
    }
    const t = setTimeout(next, SLIDE_MS);
    return () => clearTimeout(t);
  }, [openIdx, playing, next]);

  // Reset playing state when opening
  const handleOpen = (i) => {
    setPlaying(true);
    setOpenIdx(i);
  };

  return (
    <section className="py-16 md:py-24 bg-splash-cream">
      <style>{`
        @keyframes splash-progress { from { width: 0% } to { width: 100% } }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">
              <Camera className="w-3.5 h-3.5" /> Photo Gallery
            </div>
            <h2 className="font-display font-bold text-3xl md:text-5xl text-splash-green leading-tight">
              Coaches & swimmers in action
            </h2>
          </div>
          <p className="max-w-md text-splash-green/75">
            A peek inside our small, focused classes &mdash; real lessons, real smiles, real progress.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
          {gallery.map((g, i) => (
            <button
              key={g.src}
              onClick={() => handleOpen(i)}
              data-testid={`gallery-thumb-${i}`}
              className={`group relative overflow-hidden rounded-2xl md:rounded-3xl bg-splash-teal/10 shadow-sm ${
                g.tall ? 'row-span-2' : ''
              }`}
              aria-label={`Open photo: ${g.caption}`}
            >
              <img
                src={g.src}
                alt={g.caption}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-3 text-white text-xs md:text-sm font-medium translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                {g.caption}
              </div>
            </button>
          ))}
        </div>
      </div>

      {openIdx !== null && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white btn-transition"
            onClick={close}
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            className="absolute top-4 left-4 flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white btn-transition text-sm font-medium"
            onClick={(e) => { e.stopPropagation(); setPlaying((p) => !p); }}
            aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
            data-testid="lightbox-playpause"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {playing ? 'Pause' : 'Play'}
          </button>

          <button
            className="absolute left-4 md:left-8 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white btn-transition"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <div className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              key={gallery[openIdx].src}
              src={gallery[openIdx].src}
              alt={gallery[openIdx].caption}
              className="max-h-[75vh] w-auto rounded-2xl shadow-2xl object-contain animate-[fadeIn_500ms_ease-out]"
              style={{ animation: 'fadeIn 500ms ease-out' }}
            />
            <style>{`@keyframes fadeIn { from { opacity: 0; transform: scale(0.98) } to { opacity: 1; transform: scale(1) } }`}</style>

            {/* Progress bar */}
            <div className="mt-4 w-full max-w-md h-1 rounded-full bg-white/15 overflow-hidden" data-testid="lightbox-progress-track">
              <div
                ref={barRef}
                data-testid="lightbox-progress-bar"
                className="h-full bg-splash-coral"
                style={{ width: playing ? '0%' : '100%' }}
              />
            </div>

            <div className="mt-3 text-white/90 text-sm md:text-base text-center max-w-2xl">
              {gallery[openIdx].caption}
            </div>
            <div className="mt-1 text-white/50 text-xs" data-testid="lightbox-counter">
              {openIdx + 1} / {gallery.length}
            </div>
          </div>

          <button
            className="absolute right-4 md:right-8 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white btn-transition"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
}
